import "@/styles/index.css";
import { i18n } from "@/i18n";
import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { createQueryClient, setUnauthenticatedHandler } from "@/app/query-client";
import { noteUnauthenticated, setSessionEndedHandler } from "@/lib/auth/session";
import { routeTree } from "./routeTree.gen";

const queryClient = createQueryClient();

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: "intent",
  // Query owns freshness; the router re-runs loaders whenever asked.
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

// A 401 on an active session ends it. Invalidate, and the guard sends the
// reader to sign-in, keeping the page they were on as the way back.
// A 401 with no active token is a sign-in refusal or a spent interim token;
// `noteUnauthenticated` leaves those to the sign-in screen.
setSessionEndedHandler(() => {
  void router.invalidate();
});
setUnauthenticatedHandler(() => {
  noteUnauthenticated(queryClient);
});

async function enableMocking(): Promise<void> {
  // __MOCK_BUILD__ is replaced by a literal at build time (vite.config.ts), so a
  // live build contains no mock worker and no fixtures.
  if (!__MOCK_BUILD__) return;
  const { worker } = await import("@/api/msw/browser");
  await worker.start({ onUnhandledRequest: "bypass", quiet: true });
}

function renderApp(): void {
  const root = document.getElementById("root");
  if (!root) throw new Error("index.html has no #root element");
  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} />
      </QueryClientProvider>
    </StrictMode>,
  );
}

/**
 * Only a mock build can land here, and it cannot run without its worker — a
 * white screen says nothing, so the failure is drawn. This is not a server
 * error: the worker never started, so there is no `message` to print.
 */
function renderMockFailure(error: unknown): void {
  console.error(error);
  const root = document.getElementById("root");
  if (!root) return;
  createRoot(root).render(
    <div role="alert" className="grid min-h-dvh place-items-center bg-surface p-s6">
      <p className="max-w-md text-center text-fs4 text-ink-1">{i18n.t("app.mockUnavailable")}</p>
    </div>,
  );
}

void enableMocking().then(renderApp).catch(renderMockFailure);
